import { Marker } from "react-map-gl/mapbox"
import { Tooltip } from "antd"
import { AddRouteJobsFilters, Job, getFilteredJobs, hasJobError, hasNoWorkAreas, hasNoApprovedWorkAreas } from "../routeAddModel"
import { AddJobsRecord } from "../../routingApi"

export type Props = {
  coords: any
  job: Job
  isSelected: boolean
  handleMarkerClick: (coords: any, e: any) => void
  selectedJobs: number[]
  jobs: AddJobsRecord[]
  filters: AddRouteJobsFilters
  filteredJobs?: Job[] // Optional pre-filtered jobs to avoid re-filtering
}

const getJobsAtLocation = (coords: string, jobs: AddJobsRecord[], filters: AddRouteJobsFilters, selectedJobs: number[]): Job[] => {
  const jobsWithCoordinates = getFilteredJobs(jobs, filters, selectedJobs)
  .filter(job => job.latitude && job.longitude) || []

  return jobsWithCoordinates.filter(job => coords === `${job.longitude},${job.latitude}`) || [];
};

const AddJobsMapMarker = ({ coords, job, isSelected, handleMarkerClick, selectedJobs, jobs, filters, filteredJobs }: Props) => {
  const jobsAtLocation = filteredJobs
    ? filteredJobs.filter(job => coords === `${job.longitude},${job.latitude}`)
    : getJobsAtLocation(coords, jobs, filters, selectedJobs);
  const hasMultipleJobs = jobsAtLocation?.length && jobsAtLocation?.length > 1;
  const allJobsDisabled = jobsAtLocation?.every(job => hasJobError(job) || hasNoWorkAreas(job) || hasNoApprovedWorkAreas(job));

  const getDisabledReason = () => {
    if (!allJobsDisabled) return null;

    if (jobsAtLocation?.every(job => hasJobError(job))) {
      return "Incomplete address";
    }
    if (jobsAtLocation?.some(job => hasNoWorkAreas(job))) {
      return hasMultipleJobs ? "Jobs have no work areas" : "Job has no work areas";
    }
    if (jobsAtLocation?.some(job => hasNoApprovedWorkAreas(job))) {
      return hasMultipleJobs ? "Jobs have no approved work areas" : "Job has no approved work areas";
    }
    return null;
  };

  const tooltipText = getDisabledReason();

  let markerColor = "#ADC6FF"; // Lighter blue for unselected
  let borderColor = "#061178"; // Dark blue border for unselected
                
  if (isSelected || jobsAtLocation?.some(job => selectedJobs.includes(job.jobId))) {
    markerColor = "#FFCABD"; // Lighter red for selected jobs
     borderColor = "#FF5C35"; // Orange-red border for selected
  }

  const markerStyle = {
    position: 'relative',
    width: '30px',
    height: '45px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    opacity: allJobsDisabled ? 0.5 : 1,
    cursor: allJobsDisabled ? 'not-allowed' : 'pointer'
  } as const;

  const pinHeadStyle = {
    width: '30px',
    height: '30px',
    backgroundColor: markerColor,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '2px solid',
    borderColor: borderColor,
    zIndex: 2,
    position: 'relative'
  } as const;

  const innerCircleStyle = {
    width: '10px',
    height: '10px',
    backgroundColor: 'white',
    borderRadius: '50%',
    border: `3px solid ${borderColor}`,
    position: 'absolute'
  } as const;

  const pinTailStyle = {
    width: '20px',
    height: '20px',
    backgroundColor: markerColor,
    clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
    marginTop: '-5px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
    zIndex: 1
  }

  const markerContent = (
    <div style={markerStyle}>
      {/* Pin head (circle) */}
      <div style={pinHeadStyle}>
        {hasMultipleJobs ? (
          <span style={{
            color: '#000000',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {jobsAtLocation?.length || 0}
          </span>
        ) : (
          <div style={innerCircleStyle} />
        )}
      </div>

      {/* Pin tail (triangle) */}
      <div style={pinTailStyle} />
    </div>
  );

  return <Marker
    key={`${job.jobId}-${isSelected}`}
    latitude={job.latitude || 0}
    longitude={job.longitude || 0}
    anchor="bottom"
    onClick={(e) => handleMarkerClick(coords, e)}
  >
    {tooltipText ? (
      <Tooltip title={tooltipText}>
        {markerContent}
      </Tooltip>
    ) : (
      markerContent
    )}
  </Marker>
}

export default AddJobsMapMarker